import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db89lbhrh5tg6socjrqg';
const MAX_MESSAGE = 500;

export default function FeedbackForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // 反垃圾蜜罐：若被填充则视为机器人，给出与正常提交一致的反馈，不发送任何请求
    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('');
      form.reset();
      return;
    }

    setStatus('submitting');
    setFormError('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试。';

      if (response.ok && code === 'OK') {
        setStatus('success');
        setMessage('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          String(serverMsg).toLowerCase().includes('spam') ? '提交内容未通过校验，请检查后重试。' : String(serverMsg)
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，暂时无法提交，请稍后再试。');
    }
  };

  const field = 'w-full h-11 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all placeholder:text-foreground-400';

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
      <h2 className="font-display text-xl font-bold text-foreground-950">意见反馈</h2>
      <p className="mt-1.5 text-sm text-foreground-500">如果对久久热有想法或建议，欢迎留言告诉我们。</p>

      {status === 'success' ? (
        <div className="mt-6 rounded-md bg-secondary-100 border border-secondary-200 p-5 text-center">
          <span className="inline-flex w-12 h-12 rounded-full bg-secondary-500 text-background-50 items-center justify-center">
            <i className="ri-check-line text-2xl" />
          </span>
          <h3 className="mt-3 font-display text-lg font-bold text-secondary-900">提交成功</h3>
          <p className="mt-1.5 text-sm text-secondary-800">感谢你的反馈，我们会认真阅读每一条建议。</p>
          <button type="button" onClick={() => setStatus('idle')} className="mt-4 inline-flex items-center gap-1.5 h-10 px-5 rounded-md border border-secondary-300 text-secondary-800 text-sm font-semibold hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap">
            再写一条
          </button>
        </div>
      ) : (
        <form data-readdy-form onSubmit={handleSubmit} className="mt-5 space-y-4" noValidate={false}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="fb-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名 <span className="text-accent-600">*</span></label>
              <input id="fb-name" name="name" type="text" required maxLength={40} placeholder="请输入你的姓名" className={field} />
            </div>
            <div>
              <label htmlFor="fb-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱 <span className="text-accent-600">*</span></label>
              <input id="fb-email" name="email" type="email" required maxLength={80} placeholder="name@example.com" className={field} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="fb-message" className="block text-sm font-medium text-foreground-700">反馈内容 <span className="text-accent-600">*</span></label>
              <span className="text-xs text-foreground-400">{message.length} / {MAX_MESSAGE}</span>
            </div>
            <textarea
              id="fb-message"
              name="message"
              required
              maxLength={MAX_MESSAGE}
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
              placeholder="说说你对久久热的想法、想看的影视类型，或任何建议……"
              className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all placeholder:text-foreground-400 resize-none"
            />
          </div>

          {/* 反垃圾蜜罐字段，普通用户不可见、不可聚焦 */}
          <input
            type="text"
            name="website_alt"
            className="jiujiu-field-guard"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />

          {status === 'error' && formError && (
            <div className="flex items-start gap-2 rounded-md bg-accent-100 border border-accent-200 px-4 py-3">
              <i className="ri-error-warning-line text-accent-700 mt-0.5" />
              <p className="text-sm text-accent-800">{formError}</p>
            </div>
          )}

          <div className="flex items-center gap-3 pt-1">
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="inline-flex items-center gap-2 h-11 px-7 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === 'submitting' ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-lg" />提交中
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line text-lg" />提交反馈
                </>
              )}
            </button>
            <p className="text-xs text-foreground-400">我们会妥善保管你的联系方式，仅用于回复反馈。</p>
          </div>
        </form>
      )}
    </div>
  );
}